import React, { useContext } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Form, FormikContextType, FormikProvider } from 'formik';
import { Box } from '@mui/material';
import { CategoryRaw, ProductErrors } from '../../../../types';
import useBusinessInformation from '../../../../helperHooks/useBusinessInformation';
import FormikTextField from '../../../../components/formikComponents/FormikTextField';
import FormikMoneyField from '../../../../components/formikMoneyField/FormikMoneyField';
import FormikSelect from '../../../../components/formikComponents/FormikSelect';
import SideFormActions from '../../../../components/createEditComponents/SideFormActions';
import { ApiContext } from '../../../../context/ApiContext';
import { API_V1_HOSTNAME_KEY } from '../../../../localStorageKeys';
import ColourDisplay from '../../../../components/ColourDisplay';
import useButtonColours from '../../../../helperHooks/useButtonColours';
import Loader from '../../../../components/Loader';

const ProductForm = ({
  formik,
  cancelAction,
  objectId,
  isSubmitting,
  errors,
  clearError,
}: {
  formik: FormikContextType<any>;
  cancelAction: () => void;
  objectId: string;
  isSubmitting: boolean;
  errors: ProductErrors;
  clearError: (errorKey: string) => void;
}) => {
  const { t } = useTranslation();
  const { apiFetch } = useContext(ApiContext);
  const { currencySymbol, isLoading: isLoadingCurrency } = useBusinessInformation();
  const buttonColours = useButtonColours();

  const { data: categoryRawData, isLoading } = useQuery<{
    Data: CategoryRaw[];
    Metadata: {
      Page: number;
      Limit: number;
      TotalPages: number;
      TotalRecords: number;
      CurrentPageSize: number;
    };
  }>('categories', () =>
    apiFetch(`${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/categories`)
  );

  const categoriesAvailable = categoryRawData?.Data?.map((row) => ({
    label: row.Name,
    value: row.Id?.toString(),
  }));

  return isLoading || isLoadingCurrency ? (
    <Loader ariaBusy={isLoading || isLoadingCurrency} />
  ) : (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <FormikTextField
          label={t('screens.products.products.name')}
          id="name"
          name="name"
          required
          objectId={objectId}
          formName="products"
          error={errors?.name}
          clearError={clearError}
        />
        <FormikSelect
          label={t('screens.products.products.category')}
          id="categoryId"
          name="categoryId"
          formName="products"
          isLoading={isLoading}
          options={categoriesAvailable || []}
          required
          loadingText="screens.products.products.loadingCategories"
        />
        <FormikMoneyField
          label={t('screens.products.products.salePrice')}
          id="salePrice"
          formName="products"
          name="salePrice"
          currencySymbol={currencySymbol}
          objectId={objectId}
          error={errors?.salePrice}
          clearError={clearError}
          fieldDefaultValue={0}
          required
        />
        <FormikMoneyField
          label={t('screens.products.products.costPrice')}
          id="costPrice"
          formName="products"
          name="costPrice"
          currencySymbol={currencySymbol}
          objectId={objectId}
          error={errors?.costPrice}
          clearError={clearError}
          fieldDefaultValue={0}
          required
        />
        <FormikSelect
          label={t('screens.products.products.colour')}
          id="buttonColourId"
          required
          name="buttonColourId"
          options={buttonColours}
          formName="products"
          objectId={objectId}
          error={errors?.buttonColourId}
          clearError={clearError}
          renderOption={(props, option) => (
            <Box {...props}>
              <ColourDisplay
                name={option.label}
                colour={option.metadata}
                renderBorder={option.label === 'None'}
              />
            </Box>
          )}
        />
        <SideFormActions cancelAction={cancelAction} isSubmitting={isSubmitting} />
      </Form>
    </FormikProvider>
  );
};
export default ProductForm;
